import cn from 'classnames'
import { FC, useCallback, useLayoutEffect, useState } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'

import styles from './EditableArea.module.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement & HTMLInputElement> {
  value: string
  onSave: (value: string) => void
}

export const EditableArea: FC<Props> = ({ value, onSave, ...props }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [text, setText] = useState(value)

  const onValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    setText(e.target.value)
  }, [])

  const onEditClick = useCallback(() => {
    setIsEditing(true)
  }, [])

  const onSaveClick = useCallback(() => {
    setIsEditing(false)
    onSave(text)
  }, [onSave, text])

  const onContainerClick = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  useLayoutEffect(() => {
    setText(value)
  }, [value])

  return (
    <span className={styles.container} onClick={onContainerClick}>
      <RenderWhen condition={isEditing}>
        <span className={cn(styles.content)}>
          <input {...props} className={styles.input} value={text} onChange={onValueChange} />
          <span onClick={onSaveClick} className={styles.iconButton} role="button" tabIndex={0}>
            <span className={styles.icon}>
              <i className={cn('kbase kbase-save')} />
            </span>
          </span>
        </span>
      </RenderWhen>
      <RenderWhen condition={!isEditing}>
        <span className={cn(styles.content)}>
          <span className={styles.text}>{text}</span>
          <span className={styles.iconButton} onClick={onEditClick} role="button" tabIndex={0}>
            <span className={styles.icon}>
              <i className={cn('kbase kbase-edit')} />
            </span>
          </span>
        </span>
      </RenderWhen>
    </span>
  )
}
